import React, { ReactNode, useEffect, useState } from 'react'
import logo from '@assets/images/logo-lb.png'
import { BsHeadphones } from 'react-icons/bs'
import { IoCloseOutline } from 'react-icons/io5'
import { ReactComponent as SuperHeroImg } from '@assets/images/superhero.svg'
import { InputToggle } from '@components/Form/InputToggle'
import { PricingCard } from '@features/pricing/PricingCard'
import {
  ContactSection,
  ContactSectionText,
  ContactSectionTitle,
  ContactSectionWrapper,
  ContentWrapper,
  InfoImgWrapper,
  InfoSection,
  InfoTextWrapper,
  PriceTableWrapper,
  PriceTableWrapperMessage,
  PricingHeader,
} from '@features/pricing/PricingCardControllers/styled'
import { Button } from '@components/Basic/ButtonNbe'
import { CreditCardBox } from '@features/billing/CreditCardBox'
import { SimplePage } from '@app/layouts/SimplePage'
import { useGetBillingPlans } from '@app/api/getBillingPlans'
import { Loader } from '@components/Basic/Loader'
import { useGetUserInfo } from '@app/api/getUserInfo'
import { FreemiumControllers } from '@features/pricing/PricingCardControllers/FremiumControllers'
import { BusinessController } from '@features/pricing/PricingCardControllers/BusinessController'
import { ModalChangePlan } from '@features/pricing/ModalChangePlan'
import { ProControllers } from '@features/pricing/PricingCardControllers/ProControllers'
import { ApiPlan, DowngradeType } from 'BackendApi'
import { InputSelectOption } from '@components/Form/InputSelect'
import { useHistory, useLocation } from 'react-router-dom'
import { appRoutes } from '@app/routes'
import { Message } from '@components/Basic/Message'
import { parseApiError } from '@app/api/utils/error'
import { Trans, useTranslation } from 'react-i18next'
import { useLocationHash } from '@hooks/useLocationHash'
import { legacyProFeatures } from '@features/pricing/legacyProFeatures'
import queryString from 'query-string'
import {
  resetPath,
  sendToast,
} from '@app/store/actions/ApplicationConfigurationActions'
import { useAppDispatch } from '@app/store/hooks'
import { trackEvent } from '@app/dataTracking'
import { calculateDaysBetweenDates } from '@app/utils/calcDaysBetweenDates'
import { UseResetDowngradeFreemium } from '@app/api/patchResetDowngradeFreemium'
import { PlanInfoBanner } from '@features/pricing/PlanInfoBanner'
import {
  useCheckPlanAvailabilityMutate,
  ApiPlanAvailable,
} from '@app/api/checkPlanAvailability'
import { openZendeskChat } from '@app/utils/helpers'
import ReactTooltip from 'react-tooltip'
import { useGetUserModules } from '@app/api/getUserModules'
import { useSelector } from 'react-redux'
import { RootState } from '@app/index'
import { plansNamesById } from '@features/checkout/checkoutUtils'
import { lbPlansWeighted } from '@features/nbee/SimpleBridgeBuilderForm/appsCompatibility/utils'
import { useGetBillingPaymentStatus } from '@app/api/getUserBillingInvoiceStatus'
import { AmountDueBox } from '@features/billing/AmountDueBox'
import { PlanReactivationBox } from '@features/billing/PlanReactivationBox'
import { useBillingPay } from '@app/api/postBillingPay'
import { usePusherChannel } from '@hooks/usePusherChannel'

interface LocationState {
  prevPath: string
}

type PlansToHandle = 'freemium' | 'starter' | 'pro' | 'business'

export const lbPlansByName: Record<PlansToHandle, number> = {
  freemium: 6,
  pro: 7,
  business: 8,
  starter: 10,
}

const PricingPageComponent: React.FC = () => {
  const [isYearlyChecked, setIsYearlyChecked] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [proPrice, setProPrice] = useState(0)
  const [starterPrice, setStarterPrice] = useState(0)
  const [currentProPlanOptions, setCurrentProPlanOptions] = useState<ApiPlan[]>(
    []
  )
  const [currentStarterPlanOptions, setCurrentStarterPlanOptions] = useState<
    ApiPlan[]
  >([])
  const [changesSelectedPlan, setChangesSelectedPlan] = useState('')
  const [selectedPlan, setSelectedPlan] = useState<ApiPlan | undefined>()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation<LocationState>()
  const queryStringParams = queryString.parse(location.search) as {
    suggestPlan?: string
    reason?: string
  }
  const prevPath: string | null = useSelector(
    (state: RootState) => state.application.path
  )
  const { zE } = window
  const { data: billingPaymentStatus, isLoading: isLoadingBillingPaymentStatus, refetch: refetchPaymentStatus } =
    useGetBillingPaymentStatus()
  const { mutate: triggerBillingPay, isLoading: isLoadingBillingPay } = useBillingPay()
  const amountDue = billingPaymentStatus?.amountDue || 0
  const isUnpaid = billingPaymentStatus?.isUnpaid
  const hasActiveSubscription = billingPaymentStatus?.hasActiveSubscription
  const shouldShowPlanReactivationBox = isUnpaid && !hasActiveSubscription
  
  const [isListeningForPayment, setIsListeningForPayment] = useState(false)
  const handlePayNow = () => {
    setIsListeningForPayment(true)

    triggerBillingPay(undefined, {
      onSuccess: () => {
        refetchPaymentStatus()
      },
      onError: () => {
        setIsListeningForPayment(false)
      }
    })
  }


  // Plan to show in the banner plan component
  // eslint-disable-next-line prefer-const
  let bannerPlanName: PlansToHandle = 'freemium'

  useEffect(() => {
    if (prevPath) {
      const unlisten = history.listen((location) => {
        if (history.action === 'POP') {
          history.replace(prevPath)
          dispatch(resetPath())
        }
      })

      return unlisten
    }
  }, [location.pathname, prevPath])

  const suggestPlan = queryStringParams?.suggestPlan
    ? parseInt(queryStringParams.suggestPlan)
    : undefined
  const reason = queryStringParams?.reason // used for data tracking

  const {
    data: plansData,
    isLoading: isLoadingPlans,
    error: plansDataError,
  } = useGetBillingPlans()

  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserInfo()

  const { data: userModulesResponse } = useGetUserModules()

  const userModules = userModulesResponse?.modules || []
  const supportUserModule = userModules.find((m) => m.id === 'customer-support')

  const supportIsAvailable = supportUserModule?.enabled

  const { mutate: resetDowngradeFreemium, data: resetDowngradeData } =
    UseResetDowngradeFreemium()

  const plans = plansData?.data?.plans || []
  const commons = plansData?.data?.commons
  const discountPercentage = commons?.discount
  const businessPrice = commons?.businessPrice || 999
  const downgradeStatus = user?.user.downgradeStatus

  const forceShowFree = queryString.parse(location.search).showFree as
    | string
    | undefined
  const userActiveBridges = user?.usage?.bridgesActive || 0
  const isUserLegacy = user?.pricing.legacy
  const userActiveMtus = user?.usage?.mtuUsage || 0
  const userCurrentPlan = plans.find(
    (plan) => plan.id === user?.pricing.cbPlanId
  )
  
  const userId = user?.user?.userId
  const pusherChannelName = userId ? `zuora.payment.${userId}` : ''
  const pusherEventName = 'zuora.collectPayment'
  const handlePaymentStatusReceived = (eventData?: any) => {
    console.log('handlePaymentStatusReceived', eventData)
    if (eventData?.data) {
      const { status, message } = eventData.data
      const toastColor = status === 'success' ? 'positive' : 'negative'
      
      dispatch(
        sendToast({
          title: status === 'success' ? 'Success' : 'Error',
          message: message,
          color: toastColor,
        })
      )
    }
    refetchPaymentStatus()
  }
  
  const handlePusherUnsubscribe = () => {
    setIsListeningForPayment(false)
  }
  
  usePusherChannel(
    pusherChannelName,
    pusherEventName,
    isListeningForPayment && !!userId,
    true,
    handlePusherUnsubscribe,
    handlePaymentStatusReceived
  )

  const planNames = ['starter', 'pro']
  const currentPlanName =
    planNames.find((plan) => userCurrentPlan?.id.includes(plan)) || null

  const isCurrentlyInTrial = Boolean(user?.pricing.trial)
  const isTrialAvailableForUser = Boolean(user?.pricing.trialAvailable)
  const selectedUnit = isYearlyChecked ? 'year' : 'month'

  const isAppSumoPlan = user?.pricing.cbPlanId === 'appsumo-2022'
  const endOfTermsDate = user?.pricing.endOfTermsDate
  const deleteAtDate = user?.pricing.deleteAt
  const dateToUse = endOfTermsDate || deleteAtDate

  // Get timestamp current date and expiration date
  const dateTimeStamp = new Date(dateToUse as string).getTime()
  const dateNow = new Date().getTime()
  const remainDays = calculateDaysBetweenDates(dateTimeStamp, dateNow)

  const { mutateAsync: getPlanAvailable, isLoading: isCheckingAvailability } =
    useCheckPlanAvailabilityMutate(selectedPlan?.id ?? '')

  useEffect(() => {
    if (downgradeStatus !== 'no') {
      resetDowngradeFreemium()
    }
  }, [user])

  // send event to segment on mount
  useEffect(() => {
    trackEvent({
      eventName: 'PricingTableShown',
      feature: 'Pricing',
      step: 'Table',
      params: {
        custom: {
          reason,
        },
      },
    })
  }, [reason])

  useEffect(() => {
    if (!isLoadingPlans && !isLoadingUser && userCurrentPlan) {
      const monthlyPlanRegex = /_mo_/
      const isMonthlyPlan = monthlyPlanRegex.test(userCurrentPlan.id)

      if (isMonthlyPlan) {
        setIsYearlyChecked(false)
      }
      const currentPlanOptions = plans.filter(
        (plan) =>
          plan.mtuLimit === userCurrentPlan?.mtuLimit &&
          plan.bridgesLimit === userCurrentPlan?.bridgesLimit
      )

      if (currentPlanName === 'starter') {
        setStarterPrice(userCurrentPlan.price)
        setCurrentStarterPlanOptions(currentPlanOptions)
      } else if (currentPlanName === 'pro') {
        setProPrice(userCurrentPlan.price)
        setCurrentProPlanOptions(currentPlanOptions)
      }
    }
  }, [isLoadingPlans, isLoadingUser, userCurrentPlan])

  useEffect(() => {
    if (currentProPlanOptions?.length > 1) {
      const selectedUnit = isYearlyChecked ? 'year' : 'month'
      const priceToSet = currentProPlanOptions.find(
        (p) => p.period_unit === selectedUnit
      )?.price
      if (priceToSet) {
        setProPrice(priceToSet)
      }
    }
  }, [currentProPlanOptions, isYearlyChecked])

  useEffect(() => {
    if (currentStarterPlanOptions?.length > 1) {
      const selectedUnit = isYearlyChecked ? 'year' : 'month'
      const priceToSet = currentStarterPlanOptions.find(
        (p) => p.period_unit === selectedUnit
      )?.price
      if (priceToSet) {
        setStarterPrice(priceToSet)
      }
    }
  }, [currentStarterPlanOptions, isYearlyChecked])

  const matchPlan = (planName: string, planId: number): boolean => {
    const planToCompare = planName.toLowerCase()
    return planId === lbPlansByName[planToCompare as PlansToHandle]
  }

  const getPriceDescription = (planName: string) => {
    if (matchPlan(planName, lbPlansByName.freemium)) {
      return t('billing.pricing.freemiumPriceDescription')
    }
    if (
      matchPlan(planName, lbPlansByName.pro) ||
      matchPlan(planName, lbPlansByName.starter)
    ) {
      // pro plan has different description based on selected unit
      return isYearlyChecked
        ? t('billing.pricing.proPriceDescriptionYearly')
        : t('billing.pricing.proPriceDescriptionMonthly')
    } else {
      // business plan
      return t('billing.pricing.businessPriceDescription')
    }
  }

  const getPlanPrice = (planName: string, yearly: boolean) => {
    if (matchPlan(planName, lbPlansByName.freemium)) {
      return 0
    } else if (matchPlan(planName, lbPlansByName.business)) {
      return businessPrice
    } else {
      // pro prices are in cents
      const price = matchPlan(planName, lbPlansByName.starter)
        ? starterPrice
        : proPrice
      const computedPrice = yearly ? price / 12 : price
      return Math.ceil(computedPrice / 100)
    }
  }

  const getMtuOptionsByLbPlan = (planName: string): InputSelectOption[] =>
    plans
      .filter((plan) => matchPlan(planName, plan.lbPlan))
      .map((plan) => ({
        value: `${plan.mtuLimit}`,
        label: `${plan.mtuLimit.toLocaleString('en-US')} ${t(
          'billing.pricing.mtuOption'
        )}`,
        disabled: plan.mtuLimit < userActiveMtus,
      }))

  const getBridgeOptionsByLbPlan = (planName: string): InputSelectOption[] =>
    plans
      .filter((plan) => matchPlan(planName, plan.lbPlan))
      .map((plan) => ({
        value: `${plan.bridgesLimit}`,
        label: `${plan.bridgesLimit.toLocaleString('en-US')} ${t(
          'billing.pricing.bridgeOption',
          {
            count: plan.bridgesLimit,
          }
        )}`,
        disabled: plan.bridgesLimit < userActiveBridges,
      }))

  const isCurrentPlan = (planName: string): boolean => {
    // we match the first part of the cbPlanId with our PlansToHandle
    // eg: pro_yo_6_5000 -> we only take `pro`
    // this also works for freemium
    const userCurrentPlan = user?.pricing?.cbPlanId
      ? user.pricing.cbPlanId.split('_')[0]
      : ''
    return matchPlan(planName, lbPlansByName[userCurrentPlan as PlansToHandle])
  }

  const onFreePlanSelect = () => {
    setIsModalOpen(!isModalOpen)
  }

  const handleSelectionChange = (matchedPlans: ApiPlan[]) => {
    const changingPlanName =
      matchedPlans.length > 0
        ? planNames.find((plan) => matchedPlans[0].id.includes(plan))
        : null

    if (changingPlanName === 'starter') {
      setCurrentStarterPlanOptions(matchedPlans)
    } else if (changingPlanName === 'pro') {
      setCurrentProPlanOptions(matchedPlans)
    }

    // setCurrentPlanOptions(matchedPlans)
    if (changingPlanName) {
      setChangesSelectedPlan(changingPlanName)
    }
  }

  const sendToastPlanUnavailable = (
    planAvailabilityReponse: ApiPlanAvailable
  ) => {
    const isPlanAvailable = Boolean(planAvailabilityReponse?.result)
    const planNotAvailableMessageCode = planAvailabilityReponse?.code // error code in case plan is not available handled in i18n
    const planNotAvailableMessage = planAvailabilityReponse?.message // error message in case of exception during module checks

    if (!isPlanAvailable) {
      const errorMessage = planNotAvailableMessageCode
        ? t(`api.error.${planNotAvailableMessageCode}` as never)
        : t('billing.checkout.planNotAvailable')
      dispatch(
        sendToast({
          title: 'Error',
          message: planNotAvailableMessage ?? errorMessage,
          color: 'negative',
        })
      )
    }
  }

  useEffect(() => {
    if (selectedPlan?.id === undefined) {
      sendToast({
        title: 'Error',
        message: t('billing.checkout.planNotAvailable'),
        color: 'negative',
      })
      return
    }

    getPlanAvailable()
      .then((planAvailabilityReponse) => {
        const isPlanAvailable = Boolean(planAvailabilityReponse?.result)
        if (!isPlanAvailable) {
          sendToastPlanUnavailable(planAvailabilityReponse)
          return
        }
        const planForQueryString =
          selectedPlan?.id !== userCurrentPlan?.id
            ? selectedPlan?.id
            : undefined
        history.push(appRoutes.checkout.makeUrl(planForQueryString))
      })
      .catch((res) => {
        const errorResponse = res.response?.data
        sendToastPlanUnavailable(errorResponse)
      })
  }, [selectedPlan])

  const isProRecommended = isCurrentPlan('freemium') || isCurrentPlan('starter') // isUserLegacy || suggestPlan === lbPlansByName.pro

  const bannerPlan = plansData?.data?.features.find(
    (feature) => feature.name.toLowerCase() === bannerPlanName
  )

  const showBanner = !(
    (isAppSumoPlan && bannerPlanName === 'freemium') ||
    (user?.pricing.legacy && !forceShowFree && bannerPlanName === 'freemium')
  )

  const pricingCards =
    plansData?.data && user && !isLoadingPlans && !isLoadingUser
      ? plansData.data.features.map((item, idx) => {
          if (
            (isAppSumoPlan && matchPlan(item.name, lbPlansByName.freemium)) ||
            (user.pricing.legacy &&
              !forceShowFree &&
              matchPlan(item.name, lbPlansByName.freemium)) ||
            (bannerPlanName === 'freemium' &&
              matchPlan(item.name, lbPlansByName.freemium))
            // Note: business banner condition removed as bannerPlanName is currently hardcoded to 'freemium'
            // || (bannerPlanName === 'business' && matchPlan(item.name, lbPlansByName['business' as PlansToHandle]))
          ) {
            // skip freemium column when user is legacy
            return null
          }

          return (
            <PricingCard
              userData={user}
              endOfTermsDate={endOfTermsDate}
              deleteAtDate={deleteAtDate}
              remainDays={remainDays}
              key={idx}
              isYearlyChecked={isYearlyChecked}
              yearlyDiscountPercentage={discountPercentage}
              isActive={isCurrentPlan(item.name)}
              planName={item.name}
              planDescription={item.description}
              price={getPlanPrice(item.name, isYearlyChecked)}
              priceDescription={getPriceDescription(item.name)}
              features={
                isUserLegacy && matchPlan(item.name, lbPlansByName.pro)
                  ? legacyProFeatures
                  : item.mainFeatures.map((item) => item)
              }
              isRecommended={
                isProRecommended && matchPlan(item.name, lbPlansByName.pro)
              }
              isCurrentPlan={isCurrentPlan(item.name)}
              onUndoSelect={(planId) => {
                const currentPlan = plans.find((plan) => planId === plan.id)
                setSelectedPlan(currentPlan)
              }}
            >
              {matchPlan(item.name, lbPlansByName.freemium) ? (
                <FreemiumControllers
                  optionsMtu={getMtuOptionsByLbPlan(item.name)}
                  optionsBridge={getBridgeOptionsByLbPlan(item.name)}
                  isCurrentPlan={isCurrentPlan(item.name)}
                  onPlanSelect={() => onFreePlanSelect()}
                  remainDays={remainDays}
                />
              ) : matchPlan(item.name, lbPlansByName.business) ? (
                <BusinessController />
              ) : matchPlan(item.name, lbPlansByName.pro) ||
                matchPlan(item.name, lbPlansByName.starter) ? (
                <ProControllers
                  isCurrentPlan={isCurrentPlan(item.name)}
                  optionsMtu={getMtuOptionsByLbPlan(item.name)}
                  optionsBridge={getBridgeOptionsByLbPlan(item.name)}
                  onPlanSelect={(matchedPlans) => {
                    // in case current plan matches selected plan we send the user to checkout without any plan
                    // in query string

                    const selectedPlan = matchedPlans.find(
                      (p) => p.period_unit === selectedUnit
                    )
                    setSelectedPlan(selectedPlan)
                  }}
                  onSelectionChange={(matchedPlans) => {
                    handleSelectionChange(matchedPlans)
                  }}
                  plans={plans}
                  userCurrentPlan={userCurrentPlan}
                  isTrialAvailable={isTrialAvailableForUser}
                  userIsInTrial={isCurrentlyInTrial}
                  isYearlyChecked={isYearlyChecked}
                />
              ) : null}
            </PricingCard>
          )
        })
      : null

  const isLoadingAll = isLoadingPlans || isLoadingUser || isLoadingBillingPaymentStatus
  const apiError = plansDataError || plansDataError || userError
  const isPageReady = !isLoadingAll && !apiError
  useLocationHash(isPageReady)

  if (isLoadingAll) {
    return <Loader $active $size={'large'} />
  }

  if (apiError) {
    return (
      <div>
        <Message $status={'error'}>{parseApiError(apiError).message}</Message>
      </div>
    )
  }

  const trialEndMessageMap: Record<
    string,
    { title: string; intro: ReactNode }
  > = {
    auto: {
      title: t('billing.pricing.pageTitleTrialEnded'),
      intro: t('billing.pricing.pageIntroTrialEnded'),
    },
    manual: {
      title: t('billing.pricing.pageTitleTrialManual'),
      intro: t('billing.pricing.pageIntroTrialManual'),
    },
    default: {
      title: t('billing.pricing.pageTitle'),
      intro: (
        <Trans
          i18nKey={'billing.pricing.pageIntro'}
          components={{ a: <a /> }}
        />
      ),
    },
  }

  const { title: pageTitle, intro: pageIntro } =
    trialEndMessageMap[downgradeStatus as DowngradeType] ||
    trialEndMessageMap.default

  return (
    <SimplePage>
      <PricingHeader>
        <img src={logo} alt='company-logo' width={180} />
        <IoCloseOutline
          size={36}
          onClick={() => {
            if (prevPath) {
              history.push(prevPath)
              dispatch(resetPath())
            } else {
              window.location.href = process.env.REACT_APP_V1_URL!
            }
          }}
        />
      </PricingHeader>
      <InfoSection>
        <InfoTextWrapper>
          <>
            <h2>{pageTitle}</h2>
            <p>{pageIntro}</p>
          </>
        </InfoTextWrapper>
        <InfoImgWrapper>
          <SuperHeroImg />
        </InfoImgWrapper>
      </InfoSection>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <InputToggle
          $variant={'dark'}
          $size={'large'}
          $labelLeft={t('billing.pricing.toggleUnitMonthly')}
          $labelRight={t('billing.pricing.toggleUnitYearly', {
            discountPercentage,
          })}
          $isChecked={isYearlyChecked}
          onChange={() => {
            setIsYearlyChecked(!isYearlyChecked)
          }}
        />
      </div>

      <ContentWrapper>
        {isUserLegacy ? (
          <PriceTableWrapperMessage>
            {t('billing.changesNotification.legacyCase')}
          </PriceTableWrapperMessage>
        ) : (
          <></>
        )}

        {shouldShowPlanReactivationBox && (
          <PlanReactivationBox
            userCurrentPlan={userCurrentPlan}
            planId={user?.pricing?.cbPlanId}
            isLegacyPlan={isUserLegacy}
          />
        )}

        <PriceTableWrapper>{pricingCards}</PriceTableWrapper>
        {downgradeStatus !== 'no' && (
          <Button
            style={{
              textDecoration: 'underline',
              display: 'block',
              margin: '2rem auto 0',
            }}
            $variant={'link-primary'}
            onClick={() => {
              window.location.href = process.env.REACT_APP_V1_URL!
            }}
          >
            {t('billing.pricing.ctaNoThanksLink')}
          </Button>
        )}
        <ModalChangePlan isOpen={isModalOpen} onCloseModal={onFreePlanSelect} />
        {bannerPlan && showBanner ? (
          <PlanInfoBanner
            plan={bannerPlan}
            /* optionsMtu={
              bannerPlan.name && bannerPlan.name.toLowerCase() === 'freemium'
                ? getMtuOptionsByLbPlan(bannerPlan.name)
                : undefined
            }
            optionsBridge={
              bannerPlan.name && bannerPlan.name.toLowerCase() === 'freemium'
                ? getBridgeOptionsByLbPlan(bannerPlan.name)
                : undefined
            } */
            isCurrentPlan={isCurrentPlan(bannerPlan.name)}
            onPlanSelect={onFreePlanSelect}
            isDisabled={!!endOfTermsDate}
          />
        ) : null}

        <ContactSection>
          <BsHeadphones />
          <ContactSectionWrapper>
            <ContactSectionTitle>
              {t('billing.pricing.needHelpTitle')}
            </ContactSectionTitle>
            <ContactSectionText>
              {t('billing.pricing.needHelpText')}
            </ContactSectionText>
          </ContactSectionWrapper>

          {!supportIsAvailable && (
            <ReactTooltip
              id='support-button-pricing'
              place='top'
              type='dark'
              textColor='white'
              effect='solid'
            >
              <span>{t('auth.supportTooltip')}</span>
            </ReactTooltip>
          )}
          <span data-tip data-for='support-button-pricing'>
            <Button
              onClick={() => openZendeskChat(zE)}
              $variant={'outlined-secondary'}
              $size={'large'}
              disabled={!supportIsAvailable}
            >
              {t('billing.pricing.needHelpCta')}
            </Button>
          </span>
        </ContactSection>
        {/* we need to keep the div.id since v1 will redirect user here when they need to update their credit card  (/pricing#credit-card)  */}
        <div
          id={'credit-card'}
          style={{ width: '100%', background: 'rgba(217, 222, 225, .15)' }}
        >
          <CreditCardBox />
        </div>

        {amountDue > 0 && hasActiveSubscription && (
          <AmountDueBox amount={amountDue} onPayClick={handlePayNow} isLoading={isLoadingBillingPay} />
        )}
      </ContentWrapper>
    </SimplePage>
  )
}

export default PricingPageComponent
